import { yupResolver } from '@hookform/resolvers/yup';
import { alpha, Autocomplete, Box, Card, Grid, IconButton, Stack, TableCell, TableRow, TextField, Tooltip, Typography } from '@mui/material';
import { useMutation } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { Fragment, SyntheticEvent, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useBlocker, useLocation } from 'react-router';
import { InferType } from 'yup';

import { WarningRounded } from '@mui/icons-material';
import {
  EmptyContent,
  enqueueSnackbar,
  FormProvider,
  useBoolean,
  useDirtyFieldsStore,
  useFactFindConfigStore,
  useFactFindErrorStore,
  useFactFindProcessStore,
  useUserAccessStore,
  useUserDetailsStore,
} from '@zeta-core';
import {
  AIInteractionService,
  IFactFindProcessSectionType,
  IFactFindProcessStatus,
  InteractionSubSectionType,
  InvestorService,
  IPatchAIInteractionSectionRequest,
} from '@zeta-services';
import { FactFindType, IClientCardItem, IFactFindEntity, IHealthDetailsApiResponse, IHealthInfoPayloadRequest, IViewFactFindRequest } from '@zeta-types';
import { ConflictsDialog } from '../../../components/conflicts-dialog';
import { getClientFeatureAccess } from '../../../helpers/access-control-helper';
import AllTranscripts from '../all-transcripts';
import { getLabel } from '../common/commons';
import FactFindHeading from '../common/fact-find-heading';
import CommonTableLayout from '../components/common-table-layout';
import ConfirmNavigationDialog from '../components/confirmation-dialog';
import ErrorDialog from '../components/error-dialog';
import { ErrorSection } from '../components/error-section';
import { renderComponent } from '../components/render-component';
import SaveDialog from '../components/save-dialog';
import SuccessDialog from '../components/success-dialog';
import FactFindFooter from '../fact-find-layout/fact-find-footer';
import { useSourceControlContext } from '../fact-find-layout/sourcecontrol-context-component';
import Transcript from '../transcript';
import { HEALTH_TABLE_CONFIG, healthDefaultValues, healthValidationsSchema } from './form-details';
import { transformHealthData, transformToHealthDetailsRequestPayload } from './transform';

export const FieldsHead = [
  { label: 'Question', width: '50%', colSpan: 2, show: true },
  { label: 'Answer', minWidth: '50%', show: true },
];

export const HealthDetails = ({
  isClient,
  isManagedClient,
  from,
  refetch,
  clientData,
  conflictData,
  updatedClientPartnerDetails,
}: {
  isClient?: boolean;
  isManagedClient?: boolean;
  from: FactFindType;
  refetch?: () => void;
}) => {
  const userAccess = useUserAccessStore((state) => state.userAccess);
  const location = useLocation();
  const [currentTab, setCurrentTab] = useState<'client' | 'partner'>('client');
  const [conflictsDialog, setConflictsDialog] = useState<boolean>(false);
  const updateDirtyFieldsState = useDirtyFieldsStore((state) => state.updateDirtyFieldsState);

  const [confirmNavigationModal, setConfirmNavigationModal] = useState(false);
  const [showPrompt, setShowPrompt] = useState(true);

  const { accessibleEntityID } = useUserDetailsStore((state) => state.user);
  let state = (location.state || {}) as { entityData: IFactFindEntity; clientData: IClientCardItem };
  if (isClient) {
    state = { clientData: { clientID: accessibleEntityID } };
  }

  const { data: sourceControlData, mutateAsync: sourceControlMutateAsync, clientPartnerDetails } = useSourceControlContext() || {};
  const ffErrors = useFactFindErrorStore((state) => state?.ffErrors);
  const updateFFErrorStore = useFactFindErrorStore((state) => state?.updateFFErrorStore);
  const fieldErrors = ffErrors?.find((error) => error.section === InteractionSubSectionType.medical_information)?.missing_fields;

  const ffConfig = useFactFindConfigStore((state) => state?.factFindConfigDetails?.individualFactFindSectionConfig?.health);
  type healthValidationsSchemaType = InferType<typeof healthValidationsSchema>;
  const sectionsProcessDetails = useFactFindProcessStore((state) => state.sections);
  const currentSection = sectionsProcessDetails?.find((section) => section.section_name === IFactFindProcessSectionType.health_condition)?.status;
  const planningStatus = location?.state?.clientData?.planningStatus;
  const readOnly = getClientFeatureAccess(userAccess?.access, 'Advisor-Client-FF', planningStatus).isReadOnly;
  const isReadOnly =
    from === FactFindType.PARA_PLANNER ||
    currentSection === IFactFindProcessStatus.pending ||
    currentSection === IFactFindProcessStatus.started ||
    isClient ||
    readOnly;

  const methods = useForm<healthValidationsSchemaType>({
    defaultValues: healthDefaultValues,
    resolver: yupResolver(healthValidationsSchema),
    mode: 'all',
    reValidateMode: 'onChange',
  });

  const { reset } = methods;

  const viewHealthDetailsAPI = useMutation({
    mutationFn: (payload: IViewFactFindRequest) => InvestorService().viewHealthDetails(payload),

    onSuccess: (response: AxiosResponse<IHealthDetailsApiResponse>) => {
      const dataFromApi = transformHealthData(response.data.response.medicalInfomation);

      methods.reset(dataFromApi);
    },
  });

  useEffect(() => {
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      viewHealthDetailsAPI.mutateAsync({ clientID: state.clientData.clientID, forExternalUse: true });
    }
    if (from === FactFindType.AI) {
      const dataFromApi = transformHealthData(clientData?.response?.medicalInfomation);
      reset(dataFromApi);
    }
  }, [reset, from, state.clientData.clientID, clientData]);

  const isAllTranscripts = useBoolean();
  const isTranscript = useBoolean();
  const isSuccess = useBoolean(false);
  const isError = useBoolean(false);

  const isDirtyInPage = methods.formState.isDirty;

  useEffect(() => {
    if (isDirtyInPage) {
      updateDirtyFieldsState(true);
    }
  }, [isDirtyInPage, updateDirtyFieldsState]);

  const blocker = useBlocker(({ nextLocation, currentLocation }) => {
    return isDirtyInPage && currentLocation.pathname !== nextLocation.pathname;
  });

  useEffect(() => {
    if (blocker.state === 'blocked' && showPrompt) {
      setConfirmNavigationModal(true);
    } else {
      blocker?.proceed?.();
    }
  }, [blocker, showPrompt]);

  const handleDiscardAndProceed = () => {
    setConfirmNavigationModal(false);
    blocker?.proceed?.();
    setShowPrompt(false);
    updateDirtyFieldsState(false);
  };

  const handleCloseNavigationModal = () => {
    setConfirmNavigationModal(false);
    blocker?.reset?.();
    setShowPrompt(true);
  };

  const handleChangeTab = (_event: SyntheticEvent, value: 'client' | 'partner') => {
    setCurrentTab(value);
  };

  const captureHealthDetailsApi = useMutation({
    mutationFn: (payload: IHealthInfoPayloadRequest) => InvestorService().captureHealthDetails(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      viewHealthDetailsAPI.mutateAsync({ clientID: state?.clientData?.clientID, forExternalUse: true });
      sourceControlMutateAsync({ clientID: state?.clientData?.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Health details saved successfully', { variant: 'success' });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const captureHealthDetailsApiAI = useMutation({
    mutationFn: (payload: IPatchAIInteractionSectionRequest) => AIInteractionService().patchClientData(payload),

    onSuccess: () => {
      methods.reset({}, { keepValues: true });
      refetch?.();
      sourceControlMutateAsync({ clientID: state.clientData.clientID });
      blocker?.reset?.();
      setShowPrompt(false);
      enqueueSnackbar('Health details saved successfully', { variant: 'success' });
      updateFFErrorStore({ ffErrors: null });
    },

    onError: () => {
      isError.onTrue();
      setTimeout(() => {
        isError.onFalse();
      }, 3000);
    },
  });

  const onSubmit = (formData: healthValidationsSchemaType) => {
    const payload = { clientID: state?.clientData?.clientID, ...transformToHealthDetailsRequestPayload(formData) };
    if ([FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from)) {
      captureHealthDetailsApi.mutateAsync(payload);
    }
    if (from === FactFindType.AI) {
      const payloadAI = {
        section: InteractionSubSectionType.medical_information,
        client_id: state.clientData.clientID,
        data: { response: { medicalInfomation: transformToHealthDetailsRequestPayload(formData) } },
      };
      captureHealthDetailsApiAI.mutate(payloadAI);
    }
  };

  const tabSections: ('client' | 'partner')[] = ['client', 'partner'];

  const getClientsOptions = tabSections.map((type) => ({
    label: getLabel(
      { value: type, label: type.charAt(0).toUpperCase() + type.slice(1) },
      [FactFindType.FACT_FIND, FactFindType.PARA_PLANNER].includes(from) && !isClient ? clientPartnerDetails : updatedClientPartnerDetails
    ),
    value: type,
  }));

  return (
    <Box>
      {isTranscript.value && <Transcript section="HealthCondition" isTranscript={isTranscript} />}
      {isAllTranscripts.value && <AllTranscripts isAllTranscripts={isAllTranscripts} />}
      {!isAllTranscripts.value && !isTranscript.value && (
        <FormProvider methods={methods} onSubmit={methods.handleSubmit(onSubmit)}>
          <>
            <Stack direction="row" alignItems="center">
              <FactFindHeading
                sectionName="Health Details"
                isAllTranscripts={isAllTranscripts}
                isTranscript={isTranscript}
                hideButton={sourceControlData?.dataSourceMap?.HealthCondition === 'ManualInput'}
                hideAllButton={isClient || [FactFindType.AI, FactFindType.PARA_PLANNER].includes(from) || isManagedClient}
              />

              {conflictData?.length > 0 && (
                <Tooltip title="Click here to view all the conflicts">
                  <IconButton
                    color="error"
                    size="small"
                    onClick={() => {
                      setConflictsDialog(true);
                    }}
                    sx={{ marginTop: '-12px' }}
                  >
                    <WarningRounded />
                  </IconButton>
                </Tooltip>
              )}
            </Stack>

            {fieldErrors?.length > 0 && <ErrorSection errors={fieldErrors} />}

            <Stack sx={{ flexDirection: 'row', alignItems: 'center', mb: 2.5 }}>
              <Autocomplete
                options={
                  from === FactFindType.PARA_PLANNER ? getClientsOptions.filter((e) => !['Client', 'Partner', 'Joint'].includes(e.label)) : getClientsOptions
                }
                id="healthDropdown"
                value={getClientsOptions.find((option) => option.value === currentTab)?.label ?? ''}
                sx={{ width: 300 }}
                onChange={(e, v) => handleChangeTab(e, v?.value)}
                getOptionLabel={(option) => option?.label || option}
                renderInput={(params) => <TextField {...params} />}
                disableClearable={true}
              />
            </Stack>

            <Stack marginTop={1.5} marginBottom={20}>
              {['client', 'partner'].map((tab) => {
                const info = viewHealthDetailsAPI.data?.data?.response?.medicalInfomation;
                const details = (tab === 'client' && info?.clientHealthCondition) || (tab === 'partner' && info?.partnerHealthCondition);

                return (
                  currentTab === tab &&
                  (isReadOnly && !details ? (
                    <Card>
                      <Stack direction="row" justifyContent="center" alignItems="center" height="60vh">
                        <EmptyContent description="No policies information has been captured as part of the fact-finding process." />
                      </Stack>
                    </Card>
                  ) : (
                    <Fragment key={tab}>
                      <CommonTableLayout tableHead={FieldsHead} borderToBeGiven>
                        <>
                          {HEALTH_TABLE_CONFIG.map((component, index) => {
                            return (
                              ((ffConfig?.fields && ffConfig?.fields[component.name]?.active) ?? true) && (
                                <TableRow key={component.label} sx={{ borderBottom: `1px solid ${alpha('#919EAB', 0.16)} !important`, verticalAlign: 'top' }}>
                                  <TableCell colSpan={2} id={`question-${index}`}>
                                    <Typography variant="caption" color="text.secondary" sx={{ py: 2.063, px: 1.75, display: 'flex', alignItems: 'center' }}>
                                      {component.label}
                                    </Typography>
                                  </TableCell>
                                  {component.twoFields && (
                                    <TableCell id="test">
                                      <Grid container alignItems="center">
                                        {component.twoFields.map((field, index) => {
                                          return (
                                            <Grid
                                              key={field.label}
                                              size={{
                                                sm: 12,
                                                md: index === 0 ? 3 : 9,
                                              }}
                                            >
                                              {renderComponent(field, `${tab}.${component.name}.${field.name}`, {}, isClient || isReadOnly)}
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </TableCell>
                                  )}
                                  {component.threeFields && (
                                    <TableCell>
                                      <Grid container alignItems="center">
                                        {component.threeFields.map((field, index) => {
                                          return (
                                            <Grid
                                              key={field.label}
                                              size={{
                                                sm: 12,
                                                md: index % 2 === 0 ? 3 : 9,
                                              }}
                                            >
                                              {field?.component && (
                                                <>{renderComponent(field, `${tab}.${component.name}.${field.name}`, {}, isClient || isReadOnly)}</>
                                              )}
                                            </Grid>
                                          );
                                        })}
                                      </Grid>
                                    </TableCell>
                                  )}
                                  {!component.threeFields && !component.twoFields && (
                                    <>{renderComponent(component, `${tab}.${component.name}`, {}, isClient || isReadOnly)}</>
                                  )}
                                </TableRow>
                              )
                            );
                          })}
                        </>
                      </CommonTableLayout>
                    </Fragment>
                  ))
                );
              })}
            </Stack>
          </>
          {isClient ? null : (
            <>
              {[FactFindType.FACT_FIND, FactFindType.AI].includes(from) && (
                <FactFindFooter isManagedClient={isManagedClient} from={from} hideNextStep={from === FactFindType.AI} hideSave={isReadOnly} />
              )}
              <SaveDialog open={captureHealthDetailsApi.isPending} />
              <SuccessDialog open={isSuccess.value} />
              <ErrorDialog open={isError.value} />
              <ConfirmNavigationDialog open={confirmNavigationModal} handleClose={handleCloseNavigationModal} data={{ handleDiscardAndProceed, blocker }} />
              <ConflictsDialog
                open={conflictsDialog}
                handleClose={() => setConflictsDialog(false)}
                tableData={conflictData || []}
                isPending={false}
                title={'Health Details'}
                refetch={refetch}
              />
            </>
          )}
        </FormProvider>
      )}
    </Box>
  );
};
